import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  MoodCheckIn,
  CreateMoodCheckInRequest,
} from '../types/mood-check-in.types';

// Query key factory — follows pattern from useCheckIn.ts and useActivityStats.ts
export const moodCheckInKeys = {
  all: ['moodCheckIns'] as const,
  history: (days: number) => [...moodCheckInKeys.all, 'history', days] as const,
};

/**
 * Fetch mood history for the authenticated user.
 * Returns the last N days of mood data, ordered by date descending (newest first).
 */
export function useMoodHistory(days: number = 5, enabled: boolean = true) {
  return useQuery<MoodCheckIn[]>({
    queryKey: moodCheckInKeys.history(days),
    queryFn: () => api.getMoodHistory(days),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create or update a mood check-in for the authenticated user.
 * Invalidates mood history cache so the Moods Report card updates.
 *
 * Fire-and-forget usage:
 * ```ts
 * const { mutate } = useCreateMoodCheckIn();
 * mutate({ emotion: 'sunny' }); // Non-blocking, no await
 * ```
 */
export function useCreateMoodCheckIn() {
  const queryClient = useQueryClient();

  return useMutation<MoodCheckIn, Error, CreateMoodCheckInRequest>({
    mutationFn: (data) => api.createMoodCheckIn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: moodCheckInKeys.all,
      });
    },
    onError: (error) => {
      // Silent failure for fire-and-forget usage
      console.error('Mood check-in failed (silent):', error);
    },
  });
}
